import { Asset, Order } from "@amzn/ito-client";
import { Row } from "@amzn/stencil-react-components/layout";
import { AcmeItemDetailsIntegration, ExtendedOrderItem, OrderWithExtendedItems } from "common/types/orders-types";
import { getString } from "common/uistringlabels/uiStringUtils";
import { getUserOS } from "lib/helpers/user-agents";
import React, { FunctionComponent } from "react";

import { InstallButton } from "./components/InstallButton";
import { InstallSection } from "./components/InstallSection";

export interface AcmeInstallButtonsProps {
  asset: Asset;
  order?: Order;
}

// Type guard to check if an order has the expected structure
function isOrderWithExtendedItems(order: unknown): order is OrderWithExtendedItems {
  return typeof order === "object" && order !== null && "items" in order && Array.isArray((order as any).items);
}

/**
 * Component to render install buttons for ACME deep links
 */
export const InstallButtons: FunctionComponent<{ acmeDeepLinks: AcmeItemDetailsIntegration }> = ({ acmeDeepLinks }) => {
  const windowsUrl = acmeDeepLinks?.windowsDeepLinkUrl?.trim();
  const macOsUrl = acmeDeepLinks?.macOsDeepLinkUrl?.trim();

  // Don't render if no valid URLs
  if (!windowsUrl && !macOsUrl) {
    return null;
  }

  const handleInstallClick = (url: string) => {
    window.location.href = url;
  };

  // Get user's OS to determine which button to show
  const userOS = getUserOS() || [];

  // Helper function to get the appropriate deep link URL for user's OS
  const getDeepLinkForUserOS = () => {
    // Check if user is on Windows
    if (userOS.includes("Windows")) {
      return windowsUrl;
    }

    // Check if user is on Mac (any Mac variant)
    if (userOS.some((os) => ["MAC", "MacOS", "Mac"].includes(os))) {
      return macOsUrl;
    }

    return null;
  };

  const userSpecificUrl = getDeepLinkForUserOS();
  const isWindows = userOS.includes("Windows");

  // If we can detect the user's OS and have the appropriate URL, show only that button
  if (userSpecificUrl) {
    return (
      <InstallSection
        title={getString("ordering.installSoftware")}
        description={getString("ordering.installSoftwareDescription")}
      >
        <Row>
          <InstallButton
            url={userSpecificUrl}
            label={getString("ordering.install")}
            testId={isWindows ? "install-windows-button" : "install-macos-button"}
            onClick={handleInstallClick}
          />
        </Row>
      </InstallSection>
    );
  }

  // Fallback: If we can't detect OS or don't have the right URL, show available options
  if (windowsUrl && macOsUrl) {
    return (
      <InstallSection
        title={getString("ordering.installSoftware")}
        description={getString("ordering.installSoftwareDescriptionBoth")}
      >
        <Row gridGap="S300">
          <InstallButton
            url={windowsUrl}
            label={getString("ordering.installForWindows")}
            testId="install-windows-button"
            onClick={handleInstallClick}
          />
          <InstallButton
            url={macOsUrl}
            label={getString("ordering.installForMacOS")}
            testId="install-macos-button"
            onClick={handleInstallClick}
          />
        </Row>
      </InstallSection>
    );
  }

  // Single OS option
  if (windowsUrl) {
    return (
      <InstallSection
        title={getString("ordering.installSoftware")}
        description={getString("ordering.installSoftwareDescriptionWindows")}
      >
        <Row>
          <InstallButton
            url={windowsUrl}
            label={getString("ordering.installForWindows")}
            testId="install-windows-button"
            onClick={handleInstallClick}
          />
        </Row>
      </InstallSection>
    );
  }

  if (macOsUrl) {
    return (
      <InstallSection
        title={getString("ordering.installSoftware")}
        description={getString("ordering.installSoftwareDescriptionMacOS")}
      >
        <Row>
          <InstallButton
            url={macOsUrl}
            label={getString("ordering.installForMacOS")}
            testId="install-macos-button"
            onClick={handleInstallClick}
          />
        </Row>
      </InstallSection>
    );
  }

  return null;
};

/**
 * Main component that handles ACME deep link detection and rendering
 */
export const AcmeInstallButtons: FunctionComponent<AcmeInstallButtonsProps> = ({ asset, order }) => {
  // Look for ACME deep links in the order items (not asset details)
  let acmeDeepLinks: AcmeItemDetailsIntegration | null = null;

  // Use type guard to ensure order has the expected structure
  if (order && isOrderWithExtendedItems(order)) {
    // Find the item that corresponds to this asset by matching asset ID
    const matchingItem = order.items?.find((item: ExtendedOrderItem) => {
      // First, try to match by asset ID in the item's assets array
      if (item.assets && asset.assetId && item.assets.includes(asset.assetId)) {
        return (
          item.details?.acmeDeepLinks &&
          (item.details.acmeDeepLinks.windowsDeepLinkUrl?.trim() || item.details.acmeDeepLinks.macOsDeepLinkUrl?.trim())
        );
      }
      return false;
    });

    // If no direct asset ID match, try additional matching criteria before fallback
    const fallbackItem = !matchingItem
      ? order.items?.find((item: ExtendedOrderItem) => {
          const hasDeepLinks =
            item.details?.acmeDeepLinks &&
            (item.details.acmeDeepLinks.windowsDeepLinkUrl?.trim() ||
              item.details.acmeDeepLinks.macOsDeepLinkUrl?.trim());

          if (!hasDeepLinks) return false;

          // This prevents incorrect matches between different software items
          return false;
        })
      : null;

    // If still no match, use any item with deep links as last resort
    const lastResortItem =
      !matchingItem && !fallbackItem
        ? order.items?.find((item: ExtendedOrderItem) => {
            return (
              item.details?.acmeDeepLinks &&
              (item.details.acmeDeepLinks.windowsDeepLinkUrl?.trim() ||
                item.details.acmeDeepLinks.macOsDeepLinkUrl?.trim())
            );
          })
        : null;

    if (!matchingItem && !fallbackItem && lastResortItem) {
      console.warn("Using last resort fallback for ACME deep links - no specific matching criteria found");
    }

    const selectedItem = matchingItem || fallbackItem || lastResortItem;
    if (selectedItem?.details?.acmeDeepLinks) {
      acmeDeepLinks = selectedItem.details.acmeDeepLinks;
    }
  }

  // Render install buttons if available
  if (acmeDeepLinks) {
    return <InstallButtons acmeDeepLinks={acmeDeepLinks} />;
  }

  return null;
};
